import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import { contentTrackingAPIClient } from '@/marketing/infrastructure/influencer/campaign/contentTracking/contentTrackingAPIClient';
import { campaignPipelineAPIClient } from '@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient';
import type { AdminContentSubmissionInput } from '@/marketing/models/campaignInfluencer';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { invalidateContentQueries } from './contentTrackingCache';
import type {
  ContentTrackingMetadataRequest,
  ContentTrackingRow,
  ContentTrackingSparkRequest,
  ContentTrackingVideoRequest,
  ContentTrackingVideoResult,
} from '@/marketing/models/contentTracking';

export function describeContentTrackingError(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 409) {
      return 'This row changed since it was loaded. Reloading…';
    }
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (message) {
      return message;
    }
  }
  return error instanceof Error ? error.message : 'Request failed';
}

// Tracking edits are revision-guarded; common Add/Delete use the server's atomic content mutations.
export function metadataBody(
  row: ContentTrackingRow,
  patch: Partial<Pick<ContentTrackingMetadataRequest, 'notes' | 'cancelled' | 'cancelReason' | 'topVideoUrls'>>,
): ContentTrackingMetadataRequest {
  return {
    expectedRevision: row.metadata.revision,
    expectedContentVersion: row.contentVersion,
    notes: row.metadata.notes,
    cancelled: row.metadata.cancelled,
    cancelReason: row.metadata.cancelReason,
    topVideoUrls: row.metadata.topVideoUrls,
    ...patch,
  };
}

export function useContentTrackingMutations(campaignId: string) {
  const queryClient = useQueryClient();
  const transitionBy = useAuthStore((state) => state.user?.email) ?? 'unknown';
  const settle = (error?: unknown) => {
    if (error) {
      toast.error(describeContentTrackingError(error));
    }
    return invalidateContentQueries(queryClient);
  };

  const reportObservation = (result: ContentTrackingVideoResult) => {
    if (result.connectedObservationReason && result.video.metricsAvailability === 'unavailable') {
      toast.message(`Saved. Connected metrics unavailable (${result.connectedObservationReason}); enter them manually.`);
    }
  };

  const addSubmission = useMutation({
    mutationFn: async ({ influencerId, submission }: { influencerId: string; submission: AdminContentSubmissionInput }) => {
      await campaignPipelineAPIClient.addCampaignInfluencerContentSubmission(campaignId, influencerId, { submission, transitionBy });
      // Read the enrollment directly: adding may remove it from the active no-link filter.
      try {
        const contentVersion = await contentTrackingAPIClient.getContentVersion(campaignId, influencerId);
        reportObservation(await contentTrackingAPIClient.saveVideo(campaignId, influencerId, {
          expectedContentVersion: contentVersion,
          url: submission.url,
        }));
      } catch {
        // The content addition succeeded. A separate observation failure must not invite a duplicate Add retry.
        toast.message('Video added. Metrics could not be refreshed; use Refresh metrics or enter them manually.');
      }
    },
    onSuccess: () => settle(),
    onError: settle,
  });

  const deleteSubmission = useMutation({
    mutationFn: ({ influencerId, url }: { influencerId: string; url: string }) =>
      campaignPipelineAPIClient.deleteCampaignInfluencerContent(campaignId, influencerId, { url, transitionBy }),
    onSuccess: () => settle(),
    onError: settle,
  });

  const saveVideo = useMutation({
    mutationFn: ({ influencerId, body }: { influencerId: string; body: ContentTrackingVideoRequest }) =>
      contentTrackingAPIClient.saveVideo(campaignId, influencerId, body),
    onSuccess: (result) => {
      reportObservation(result);
      return settle();
    },
    onError: settle,
  });

  const saveSparkAdsCode = useMutation({
    mutationFn: ({ influencerId, body }: { influencerId: string; body: ContentTrackingSparkRequest }) =>
      contentTrackingAPIClient.updateSparkAdsCode(campaignId, influencerId, body),
    onSuccess: () => settle(),
    onError: settle,
  });

  const saveMetadata = useMutation({
    mutationFn: ({ influencerId, body }: { influencerId: string; body: ContentTrackingMetadataRequest }) =>
      contentTrackingAPIClient.updateMetadata(campaignId, influencerId, body),
    onSuccess: () => settle(),
    onError: settle,
  });

  const toggleTopVideo = (row: ContentTrackingRow, url: string) => {
    const current = row.metadata.topVideoUrls;
    const topVideoUrls = current.includes(url) ? current.filter((item) => item !== url) : [...current, url];
    saveMetadata.mutate({ influencerId: row.influencerId, body: metadataBody(row, { topVideoUrls }) });
  };

  return { addSubmission, deleteSubmission, saveVideo, saveSparkAdsCode, saveMetadata, toggleTopVideo };
}
